import { Box, Chip, List, ListItemButton, ListItemText, ListSubheader, Tooltip } from '@mui/material';
import { type ReportCatalogEntry, type ReportType, reportGroups } from '@parkchirp/reporting';
import { reportNavigationStyles } from './styles';

type Props = {
	selectedReportKey: ReportType | null;
	disabled: boolean;
	onSelectReport: (reportType: ReportType) => void;
};

export const ReportNavigation = ({ selectedReportKey, disabled, onSelectReport }: Props) => {
	return (
		<Box
			component='nav'
			aria-label='Reports'
			sx={(theme) => ({
				...reportNavigationStyles(theme),
				border: 1,
				flexShrink: 0,
				py: 1.5,
				width: 300,
			})}
		>
			{reportGroups.map((group, groupIndex) => (
				<List
					key={group.key}
					dense
					disablePadding
					subheader={
						<ListSubheader
							component='div'
							disableSticky
							sx={{
								bgcolor: 'transparent',
								color: 'text.primary',
								fontSize: '0.75rem',
								fontWeight: 700,
								letterSpacing: '0.04em',
								lineHeight: 2,
								px: 2,
								pt: groupIndex === 0 ? 0.5 : 1.75,
								textTransform: 'uppercase',
							}}
						>
							{group.title}
						</ListSubheader>
					}
				>
					{group.reports.map((report: ReportCatalogEntry) => {
						const reportDisabled = disabled || !report.isAvailable;

						const item = (
							<ListItemButton
								selected={selectedReportKey === report.key}
								disabled={reportDisabled}
								onClick={() => {
									if (report.isAvailable) {
										onSelectReport(report.key);
									}
								}}
								sx={{
									borderRadius: 1,
									borderLeft: 3,
									borderColor: selectedReportKey === report.key ? 'primary.main' : 'transparent',
									mb: 0.25,
									minHeight: 36,
									mx: 1,
									pl: 2,
									pr: 1,
									'&.Mui-disabled': {
										opacity: 1,
									},
									'&.Mui-selected': {
										bgcolor: 'action.selected',
									},
									'&.Mui-selected:hover': {
										bgcolor: 'action.hover',
									},
								}}
							>
								<ListItemText
									primary={report.title}
									primaryTypographyProps={{
										fontSize: '0.875rem',
										fontWeight: selectedReportKey === report.key ? 600 : 400,
										lineHeight: 1.25,
									}}
								/>

								{!report.isAvailable && (
									<Chip
										label='Coming soon'
										size='small'
										variant='outlined'
										sx={{
											bgcolor: 'transparent',
											color: 'text.disabled',
											fontSize: '0.6875rem',
											height: 20,
											ml: 1,
										}}
									/>
								)}
							</ListItemButton>
						);

						if (report.isAvailable) {
							return <Box key={report.key}>{item}</Box>;
						}

						return (
							<Tooltip key={report.key} title='Coming soon' placement='right'>
								<Box component='span'>{item}</Box>
							</Tooltip>
						);
					})}
				</List>
			))}
		</Box>
	);
};
